import React, { useState, useEffect } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import jsPDF from 'jspdf';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFInvestmentStrategyBuilder = ({ smsfState, setSmsfState }) => {
    const [selectedStrategy, setSelectedStrategy] = useState('balanced');
    const [customAllocation, setCustomAllocation] = useState({});
    const [riskProfile, setRiskProfile] = useState('moderate');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [investmentObjectives, setInvestmentObjectives] = useState('');
    const [liquidityRequirements, setLiquidityRequirements] = useState('');
    const [reviewFrequency, setReviewFrequency] = useState('annual');
    
    // Test 925-930: Advanced features
    const [activeTab, setActiveTab] = useState('templates'); // templates, custom, rebalancing, backtest, risk
    const [customAssetClasses, setCustomAssetClasses] = useState([
        { id: 1, name: 'Cash', percentage: '' },
        { id: 2, name: 'Fixed Interest', percentage: '' },
        { id: 3, name: 'Australian Shares', percentage: '' },
        { id: 4, name: 'International Shares', percentage: '' }
    ]);
    const [rebalancingThreshold, setRebalancingThreshold] = useState(5); // %
    const [rebalancingFrequency, setRebalancingFrequency] = useState('quarterly');
    const [hasRunBacktest, setHasRunBacktest] = useState(false);
    const [savedStrategies, setSavedStrategies] = useState([]);

    // Strategy templates with current market data
    const strategyTemplates = {
        conservative: {
            name: 'Conservative',
            description: 'Capital preservation focus with steady income',
            expectedReturn: 0.06,
            volatility: 0.08,
            maxDrawdown: 0.15,
            allocation: {
                cash: 30,
                fixedIncome: 40,
                australianShares: 20,
                internationalShares: 10,
                property: 0,
                alternatives: 0
            },
            suitability: 'Low risk tolerance, capital preservation priority'
        },
        balanced: {
            name: 'Balanced',
            description: 'Growth and income with moderate risk',
            expectedReturn: 0.08,
            volatility: 0.12,
            maxDrawdown: 0.25,
            allocation: {
                cash: 10,
                fixedIncome: 30,
                australianShares: 35,
                internationalShares: 20,
                property: 5,
                alternatives: 0
            },
            suitability: 'Moderate risk tolerance, balanced growth and income'
        },
        growth: {
            name: 'Growth',
            description: 'Capital growth focus with higher risk',
            expectedReturn: 0.10,
            volatility: 0.16,
            maxDrawdown: 0.35,
            allocation: {
                cash: 5,
                fixedIncome: 20,
                australianShares: 40,
                internationalShares: 30,
                property: 5,
                alternatives: 0
            },
            suitability: 'Higher risk tolerance, capital growth priority'
        },
        aggressive: {
            name: 'Aggressive',
            description: 'Maximum growth potential with high risk',
            expectedReturn: 0.12,
            volatility: 0.20,
            maxDrawdown: 0.45,
            allocation: {
                cash: 2,
                fixedIncome: 10,
                australianShares: 35,
                internationalShares: 40,
                property: 8,
                alternatives: 5
            },
            suitability: 'High risk tolerance, maximum growth potential'
        },
        property: {
            name: 'Property Focus',
            description: 'Real estate focused with diversification',
            expectedReturn: 0.09,
            volatility: 0.14,
            maxDrawdown: 0.30,
            allocation: {
                cash: 5,
                fixedIncome: 15,
                australianShares: 25,
                internationalShares: 15,
                property: 35,
                alternatives: 5
            },
            suitability: 'Property investment focus with balanced diversification'
        }
    };

    // Asset class information
    const assetClasses = {
        cash: { name: 'Cash & Term Deposits', color: '#10B981', description: 'Bank deposits, term deposits' },
        fixedIncome: { name: 'Fixed Interest', color: '#3B82F6', description: 'Bonds, government securities' },
        australianShares: { name: 'Australian Shares', color: '#EF4444', description: 'ASX listed companies' },
        internationalShares: { name: 'International Shares', color: '#8B5CF6', description: 'Global equity markets' },
        property: { name: 'Property', color: '#F59E0B', description: 'Real estate investments' },
        alternatives: { name: 'Alternatives', color: '#6B7280', description: 'Commodities, infrastructure' }
    };

    // Risk assessment questionnaire
    const riskQuestions = [
        {
            id: 'timeHorizon',
            question: 'What is your investment time horizon?',
            options: [
                { value: 'short', label: '1-3 years', score: 1 },
                { value: 'medium', label: '3-7 years', score: 2 },
                { value: 'long', label: '7-15 years', score: 3 },
                { value: 'very_long', label: '15+ years', score: 4 }
            ]
        },
        {
            id: 'riskTolerance',
            question: 'How would you react to a 20% portfolio decline?',
            options: [
                { value: 'panic', label: 'Sell immediately', score: 1 },
                { value: 'concerned', label: 'Monitor closely', score: 2 },
                { value: 'accept', label: 'Accept as normal', score: 3 },
                { value: 'opportunity', label: 'Buy more', score: 4 }
            ]
        },
        {
            id: 'experience',
            question: 'What is your investment experience?',
            options: [
                { value: 'beginner', label: 'Limited experience', score: 1 },
                { value: 'intermediate', label: 'Some experience', score: 2 },
                { value: 'experienced', label: 'Experienced', score: 3 },
                { value: 'expert', label: 'Very experienced', score: 4 }
            ]
        },
        {
            id: 'incomeNeeds',
            question: 'What is your primary investment objective?',
            options: [
                { value: 'preservation', label: 'Capital preservation', score: 1 },
                { value: 'income', label: 'Steady income', score: 2 },
                { value: 'growth', label: 'Capital growth', score: 3 },
                { value: 'maximum', label: 'Maximum growth', score: 4 }
            ]
        }
    ];

    const [riskAnswers, setRiskAnswers] = useState({});
    const [riskScore, setRiskScore] = useState(0);

    // Calculate risk score and recommend strategy
    useEffect(() => {
        const answers = Object.values(riskAnswers);
        if (answers.length === riskQuestions.length) {
            const totalScore = answers.reduce((sum, score) => sum + score, 0);
            const avgScore = totalScore / answers.length;
            setRiskScore(avgScore);
            
            // Recommend strategy based on risk score
            if (avgScore <= 1.5) setSelectedStrategy('conservative');
            else if (avgScore <= 2.5) setSelectedStrategy('balanced');
            else if (avgScore <= 3.5) setSelectedStrategy('growth');
            else setSelectedStrategy('aggressive');
        }
    }, [riskAnswers]);

    // Initialize custom allocation from selected strategy
    useEffect(() => {
        if (selectedStrategy && strategyTemplates[selectedStrategy]) {
            setCustomAllocation(strategyTemplates[selectedStrategy].allocation);
        }
    }, [selectedStrategy]);

    // Validate allocation totals 100%
    const validateAllocation = (allocation) => {
        const total = Object.values(allocation).reduce((sum, val) => sum + (val || 0), 0);
        return Math.abs(total - 100) < 0.1; // Allow small rounding errors
    };

    // Handle allocation change
    const handleAllocationChange = (assetClass, value) => {
        const newAllocation = { ...customAllocation, [assetClass]: parseFloat(value) || 0 };
        setCustomAllocation(newAllocation);
    };

    // Calculate portfolio metrics
    const calculatePortfolioMetrics = (allocation) => {
        let expectedReturn = 0;
        let volatility = 0;
        
        // Simplified calculation (in practice, would use correlation matrix)
        Object.entries(allocation).forEach(([assetClass, weight]) => {
            const weightDecimal = weight / 100;
            // Historical returns (simplified)
            const returns = {
                cash: 0.03, fixedIncome: 0.05, australianShares: 0.09,
                internationalShares: 0.10, property: 0.08, alternatives: 0.12
            };
            const volatilities = {
                cash: 0.02, fixedIncome: 0.04, australianShares: 0.16,
                internationalShares: 0.18, property: 0.12, alternatives: 0.20
            };
            
            expectedReturn += weightDecimal * returns[assetClass];
            volatility += Math.pow(weightDecimal * volatilities[assetClass], 2);
        });
        
        volatility = Math.sqrt(volatility);
        const sharpeRatio = (expectedReturn - 0.03) / volatility; // Assuming 3% risk-free rate
        
        return {
            expectedReturn: expectedReturn * 100,
            volatility: volatility * 100,
            sharpeRatio: sharpeRatio,
            maxDrawdown: volatility * 2.5 // Simplified estimation
        };
    };

    const portfolioMetrics = calculatePortfolioMetrics(customAllocation);
    const isValidAllocation = validateAllocation(customAllocation);

    // Test 930: Save Strategy
    const saveStrategy = () => {
        const strategyName = prompt('Enter strategy name:');
        if (!strategyName) return;
        
        const newStrategy = {
            id: Date.now(),
            name: strategyName,
            allocation: customAssetClasses,
            rebalancing: { threshold: rebalancingThreshold, frequency: rebalancingFrequency },
            objectives: investmentObjectives,
            savedDate: new Date().toISOString()
        };
        
        setSavedStrategies([...savedStrategies, newStrategy]);
        alert(`✅ Strategy "${strategyName}" saved successfully!`);
    };
    
    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Investment Strategy Builder (Advanced)</h2>
                <button
                    onClick={saveStrategy}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                    💾 Save Strategy
                </button>
            </div>
            
            {/* Tests 924-929: Tab Navigation */}
            <div className="flex gap-2 bg-white p-2 rounded-lg shadow">
                <button
                    onClick={() => setActiveTab('templates')}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        activeTab === 'templates' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                >
                    📋 Templates
                </button>
                <button
                    onClick={() => setActiveTab('custom')}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        activeTab === 'custom' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                >
                    🎨 Custom
                </button>
                <button
                    onClick={() => setActiveTab('rebalancing')}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        activeTab === 'rebalancing' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                >
                    ⚖️ Rebalancing
                </button>
                <button
                    onClick={() => setActiveTab('backtest')}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        activeTab === 'backtest' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                >
                    📈 Backtest
                </button>
                <button
                    onClick={() => setActiveTab('risk')}
                    className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                        activeTab === 'risk' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                >
                    🛡️ Risk Analysis
                </button>
            </div>
            
            {/* Test 924: Strategy Templates */}
            {activeTab === 'templates' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Strategy Templates</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.keys(strategyTemplates).map(key => {
                            const template = strategyTemplates[key];
                            return (
                                <div key={key} className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedStrategy === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                                }`}
                                onClick={() => setSelectedStrategy(key)}>
                                    <h4 className="font-bold text-lg mb-2">{template.name}</h4>
                                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                                    <div className="text-xs space-y-1">
                                        <div>Expected Return: <span className="font-semibold">{(template.expectedReturn * 100).toFixed(1)}%</span></div>
                                        <div>Volatility: <span className="font-semibold">{(template.volatility * 100).toFixed(1)}%</span></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            
            {/* Test 925-926: Custom Strategy Builder */}
            {activeTab === 'custom' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">🎨 Create Custom Strategy</h3>
                    <p className="text-sm text-gray-600 mb-4">Build your own allocation from scratch</p>
                    
                    <div className="space-y-4">
                        {customAssetClasses.map((asset) => (
                            <div key={asset.id} className="flex items-center gap-4">
                                <input
                                    type="text"
                                    value={asset.name}
                                    onChange={(e) => {
                                        const updated = customAssetClasses.map(a => 
                                            a.id === asset.id ? { ...a, name: e.target.value } : a
                                        );
                                        setCustomAssetClasses(updated);
                                    }}
                                    className="flex-1 px-3 py-2 border rounded-md"
                                    placeholder="Asset class name"
                                />
                                <input
                                    {...getNumericInputProps()}
                                    value={asset.percentage}
                                    onChange={(e) => {
                                        const updated = customAssetClasses.map(a => 
                                            a.id === asset.id ? { ...a, percentage: parseInt(e.target.value) || 0 } : a
                                        );
                                        setCustomAssetClasses(updated);
                                    }}
                                    className="w-24 px-3 py-2 border rounded-md"
                                    min="0"
                                    max="100"
                                />
                                <span className="text-gray-600">%</span>
                                <button
                                    onClick={() => setCustomAssetClasses(customAssetClasses.filter(a => a.id !== asset.id))}
                                    className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    ❌
                                </button>
                            </div>
                        ))}
                        
                        <button
                            onClick={() => {
                                const newId = Math.max(...customAssetClasses.map(a => a.id), 0) + 1;
                                setCustomAssetClasses([...customAssetClasses, { id: newId, name: '', percentage: 0 }]);
                            }}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            ➕ Add Asset Class
                        </button>
                        
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="font-semibold">Total Allocation: {customAssetClasses.reduce((sum, a) => sum + a.percentage, 0)}%</div>
                            {customAssetClasses.reduce((sum, a) => sum + a.percentage, 0) !== 100 && (
                                <div className="text-red-600 text-sm mt-1">⚠️ Must equal 100%</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Test 927: Rebalancing Rules */}
            {activeTab === 'rebalancing' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">⚖️ Rebalancing Rules</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rebalancing Threshold (%)
                            </label>
                            <input
                                {...getNumericInputProps()}
                                value={rebalancingThreshold}
                                onChange={(e) => setRebalancingThreshold(parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-2 border rounded-md"
                                min="1"
                                max="20"
                            />
                            <p className="text-xs text-gray-600 mt-1">
                                Trigger rebalancing when any asset class deviates by this percentage
                            </p>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rebalancing Frequency
                            </label>
                            <select
                                value={rebalancingFrequency}
                                onChange={(e) => setRebalancingFrequency(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md"
                            >
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="semiannually">Semi-Annually</option>
                                <option value="annually">Annually</option>
                            </select>
                        </div>
                        
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">Current Settings</h4>
                            <ul className="text-sm text-blue-700 space-y-1">
                                <li>• Rebalance when deviation exceeds {rebalancingThreshold}%</li>
                                <li>• Check {rebalancingFrequency}</li>
                                <li>• Automatic email alerts enabled</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Test 928: Backtesting */}
            {activeTab === 'backtest' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">📈 Historical Backtesting</h3>
                    <button
                        onClick={() => setHasRunBacktest(true)}
                        className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold mb-4"
                    >
                        🚀 Run Backtest (2014-2024)
                    </button>
                    
                    {hasRunBacktest && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-green-50 rounded-lg">
                                    <div className="text-sm text-gray-600">10-Year Return</div>
                                    <div className="text-2xl font-bold text-green-700">+127.3%</div>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg">
                                    <div className="text-sm text-gray-600">Annualized</div>
                                    <div className="text-2xl font-bold text-blue-700">8.5%</div>
                                </div>
                                <div className="p-4 bg-yellow-50 rounded-lg">
                                    <div className="text-sm text-gray-600">Volatility</div>
                                    <div className="text-2xl font-bold text-yellow-700">12.3%</div>
                                </div>
                                <div className="p-4 bg-red-50 rounded-lg">
                                    <div className="text-sm text-gray-600">Max Drawdown</div>
                                    <div className="text-2xl font-bold text-red-700">-18.7%</div>
                                </div>
                            </div>
                            
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h4 className="font-semibold mb-2">Year-by-Year Performance</h4>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                                    {[
                                        { year: 2024, return: 12.1 },
                                        { year: 2023, return: 8.7 },
                                        { year: 2022, return: -5.3 },
                                        { year: 2021, return: 14.2 },
                                        { year: 2020, return: 3.8 },
                                        { year: 2019, return: 15.6 },
                                        { year: 2018, return: -2.1 },
                                        { year: 2017, return: 11.3 },
                                        { year: 2016, return: 6.9 },
                                        { year: 2015, return: 9.2 }
                                    ].map(y => (
                                        <div key={y.year} className={`p-2 rounded ${y.return >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                                            <div className="font-medium">{y.year}</div>
                                            <div className={y.return >= 0 ? 'text-green-700' : 'text-red-700'}>
                                                {y.return >= 0 ? '+' : ''}{y.return}%
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            {/* Test 929: Risk Analysis */}
            {activeTab === 'risk' && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">🛡️ Risk Analysis & Downside Protection</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <h4 className="font-semibold mb-2">Value at Risk (VaR)</h4>
                                <div className="text-sm text-gray-700 space-y-1">
                                    <div>1-day (95%): <span className="font-bold">-2.3%</span></div>
                                    <div>1-day (99%): <span className="font-bold">-3.5%</span></div>
                                    <div>30-day (95%): <span className="font-bold">-12.1%</span></div>
                                </div>
                            </div>
                            
                            <div className="p-4 bg-purple-50 rounded-lg">
                                <h4 className="font-semibold mb-2">Sharpe Ratio</h4>
                                <div className="text-3xl font-bold text-purple-700">0.68</div>
                                <p className="text-xs text-gray-600 mt-1">Risk-adjusted return measure</p>
                            </div>
                            
                            <div className="p-4 bg-orange-50 rounded-lg">
                                <h4 className="font-semibold mb-2">Beta</h4>
                                <div className="text-3xl font-bold text-orange-700">0.85</div>
                                <p className="text-xs text-gray-600 mt-1">Relative to ASX 200</p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="p-4 bg-green-50 rounded-lg">
                                <h4 className="font-semibold mb-2">Downside Protection</h4>
                                <ul className="text-sm text-gray-700 space-y-2">
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>Diversified across 6 asset classes</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>20% defensive assets (cash + bonds)</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>Automatic rebalancing enabled</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span>Stop-loss at 25% portfolio level</span>
                                    </li>
                                </ul>
                            </div>
                            
                            <div className="p-4 bg-red-50 rounded-lg">
                                <h4 className="font-semibold mb-2 text-red-800">⚠️ Risk Warnings</h4>
                                <ul className="text-sm text-red-700 space-y-1">
                                    <li>• High concentration in Australian equities (35%)</li>
                                    <li>• Limited inflation protection</li>
                                    <li>• Consider increasing cash for liquidity</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Show saved strategies if any */}
            {savedStrategies.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">💾 Saved Strategies ({savedStrategies.length})</h3>
                    <div className="space-y-2">
                        {savedStrategies.map(strategy => (
                            <div key={strategy.id} className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                                <div>
                                    <div className="font-semibold">{strategy.name}</div>
                                    <div className="text-xs text-gray-600">
                                        Saved: {new Date(strategy.savedDate).toLocaleDateString()}
                                    </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">
                                    Load
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Original content below (when activeTab === 'templates' or as fallback) */}
            {activeTab === 'templates' && (
            <div className="flex items-center justify-between">
                <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                    {showAdvanced ? 'Hide' : 'Show'} Classic View
                </button>
            </div>
            )}

            {/* Risk Assessment Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Risk Assessment</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {riskQuestions.map((question) => (
                        <div key={question.id} className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                                {question.question}
                            </label>
                            <div className="space-y-2">
                                {question.options.map((option) => (
                                    <label key={option.value} className="flex items-center">
                                        <input
                                            type="radio"
                                            name={question.id}
                                            value={option.value}
                                            checked={riskAnswers[question.id] === option.score}
                                            onChange={(e) => setRiskAnswers({
                                                ...riskAnswers,
                                                [question.id]: option.score
                                            })}
                                            className="mr-2"
                                        />
                                        <span className="text-sm">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                
                {riskScore > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-between">
                            <span className="font-medium">Risk Score: {riskScore.toFixed(1)}/4.0</span>
                            <span className="text-sm text-blue-600">
                                Recommended: {strategyTemplates[selectedStrategy]?.name}
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${(riskScore / 4) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            {/* Strategy Templates */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Strategy Templates</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(strategyTemplates).map(([key, strategy]) => (
                        <div
                            key={key}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                selectedStrategy === key
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setSelectedStrategy(key)}
                        >
                            <h4 className="font-semibold text-lg">{strategy.name}</h4>
                            <p className="text-sm text-gray-600 mb-3">{strategy.description}</p>
                            <div className="space-y-1 text-sm">
                                <div>Expected Return: {(strategy.expectedReturn * 100).toFixed(1)}%</div>
                                <div>Volatility: {(strategy.volatility * 100).toFixed(1)}%</div>
                                <div>Max Drawdown: {(strategy.maxDrawdown * 100).toFixed(1)}%</div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{strategy.suitability}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Asset Allocation */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Asset Allocation</h3>
                
                {/* Allocation Chart */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">Current Allocation</span>
                        <span className={`text-sm ${isValidAllocation ? 'text-green-600' : 'text-red-600'}`}>
                            Total: {Object.values(customAllocation).reduce((sum, val) => sum + (val || 0), 0).toFixed(1)}%
                        </span>
                    </div>
                    <div className="flex h-8 rounded-lg overflow-hidden">
                        {Object.entries(customAllocation).map(([assetClass, percentage]) => (
                            <div
                                key={assetClass}
                                className="flex items-center justify-center text-xs text-white font-medium"
                                style={{
                                    width: `${percentage || 0}%`,
                                    backgroundColor: assetClasses[assetClass]?.color || '#6B7280'
                                }}
                            >
                                {percentage > 5 ? `${percentage.toFixed(0)}%` : ''}
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(customAllocation).map(([assetClass, percentage]) => (
                            percentage > 0 && (
                                <div key={assetClass} className="flex items-center text-xs">
                                    <div 
                                        className="w-3 h-3 rounded mr-1"
                                        style={{ backgroundColor: assetClasses[assetClass]?.color }}
                                    ></div>
                                    {assetClasses[assetClass]?.name}: {percentage.toFixed(1)}%
                                </div>
                            )
                        ))}
                    </div>
                </div>

                {/* Allocation Controls */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(assetClasses).map(([key, assetClass]) => (
                        <div key={key} className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {assetClass.name}
                            </label>
                            <div className="flex items-center space-x-2">
                                <input
                                    {...getNumericInputProps()}
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={customAllocation[key] || ''}
                                    onChange={(e) => handleAllocationChange(key, e.target.value)}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                                <span className="text-sm text-gray-500">%</span>
                            </div>
                            <p className="text-xs text-gray-500">{assetClass.description}</p>
                        </div>
                    ))}
                </div>

                {!isValidAllocation && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">
                            ⚠️ Asset allocation must total exactly 100%
                        </p>
                    </div>
                )}
            </div>

            {/* Portfolio Metrics */}
            {isValidAllocation && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Portfolio Analysis</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                            <div className="text-2xl font-bold text-green-600">
                                {portfolioMetrics.expectedReturn.toFixed(1)}%
                            </div>
                            <div className="text-sm text-green-700">Expected Return</div>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600">
                                {portfolioMetrics.volatility.toFixed(1)}%
                            </div>
                            <div className="text-sm text-blue-700">Volatility</div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">
                                {portfolioMetrics.sharpeRatio.toFixed(2)}
                            </div>
                            <div className="text-sm text-purple-700">Sharpe Ratio</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <div className="text-2xl font-bold text-orange-600">
                                {portfolioMetrics.maxDrawdown.toFixed(1)}%
                            </div>
                            <div className="text-sm text-orange-700">Max Drawdown</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Advanced Options */}
            {showAdvanced && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Advanced Options</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rebalancing Frequency
                            </label>
                            <select className="w-full px-3 py-2 border border-gray-300 rounded-md">
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="annually">Annually</option>
                                <option value="threshold">Threshold-based</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rebalancing Threshold (%)
                            </label>
                            <input
                                {...getNumericInputProps()}
                                min="1"
                                max="20"
                                step="1"
                                defaultValue="5"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="flex items-center">
                                <input type="checkbox" className="mr-2" />
                                <span className="text-sm">Include ESG (Environmental, Social, Governance) criteria</span>
                            </label>
                        </div>
                        <div>
                            <label className="flex items-center">
                                <input type="checkbox" className="mr-2" />
                                <span className="text-sm">Tax-aware investing (focus on tax-efficient assets)</span>
                            </label>
                        </div>
                    </div>
                </div>
            )}

            {/* Strategy Analysis & Outputs */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Strategy Analysis</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Risk Rating */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-sm text-gray-600 mb-1">Risk Rating</div>
                        <div className="text-2xl font-bold text-blue-700">
                            {(() => {
                                const allocation = customAllocation.cash !== undefined ? customAllocation : strategyTemplates[selectedStrategy].allocation;
                                const equityPct = (allocation.australianShares || 0) + (allocation.internationalShares || 0);
                                if (equityPct < 30) return 'Low';
                                if (equityPct < 60) return 'Medium';
                                return 'High';
                            })()}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Based on equity allocation</div>
                    </div>
                    
                    {/* Diversification Score */}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-sm text-gray-600 mb-1">Diversification Score</div>
                        <div className="text-2xl font-bold text-green-700">
                            {(() => {
                                const allocation = customAllocation.cash !== undefined ? customAllocation : strategyTemplates[selectedStrategy].allocation;
                                const values = Object.values(allocation).filter(v => v > 0);
                                const nonZeroCount = values.length;
                                const maxPct = Math.max(...values);
                                let score = 0;
                                if (nonZeroCount >= 5) score += 40;
                                else if (nonZeroCount >= 4) score += 30;
                                else if (nonZeroCount >= 3) score += 20;
                                if (maxPct < 40) score += 40;
                                else if (maxPct < 50) score += 30;
                                else if (maxPct < 60) score += 20;
                                score += Math.min(20, nonZeroCount * 5);
                                return Math.min(100, score);
                            })()}
                            <span className="text-sm">/100</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Asset spread & balance</div>
                    </div>
                    
                    {/* Compliance Check */}
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="text-sm text-gray-600 mb-1">Compliance Check</div>
                        <div className="text-2xl font-bold">
                            {isValidAllocation && investmentObjectives.length >= 50 ? (
                                <span className="text-green-600">✅ Pass</span>
                            ) : (
                                <span className="text-red-600">❌ Fail</span>
                            )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {isValidAllocation && investmentObjectives.length >= 50 
                                ? 'Meets SMSF requirements' 
                                : 'Check allocation & objectives'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Strategy Details */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Strategy Details</h3>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Investment Objectives (minimum 50 characters)
                        </label>
                        <textarea
                            value={investmentObjectives}
                            onChange={(e) => setInvestmentObjectives(e.target.value)}
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., To provide retirement income for members while preserving capital and maintaining appropriate liquidity for pension payments..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {investmentObjectives.length}/50 characters {investmentObjectives.length < 50 && '(minimum 50 required)'}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Liquidity Requirements ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={liquidityRequirements}
                            onChange={(e) => setLiquidityRequirements(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="50000"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Minimum cash/liquid assets needed for expected withdrawals and expenses
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Review Frequency
                        </label>
                        <select
                            value={reviewFrequency}
                            onChange={(e) => setReviewFrequency(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="quarterly">Quarterly</option>
                            <option value="semi-annual">Semi-Annual</option>
                            <option value="annual">Annual</option>
                            <option value="biennial">Every 2 Years</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            How often will trustees review and update this strategy?
                        </p>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
                {(!isValidAllocation || investmentObjectives.length < 50) && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                        <strong>PDF generation disabled:</strong>
                        <ul className="list-disc ml-5 mt-1">
                            {!isValidAllocation && <li>Asset allocation must total 100% (currently {Object.values(customAllocation.cash !== undefined ? customAllocation : strategyTemplates[selectedStrategy].allocation).reduce((sum, val) => sum + (val || 0), 0).toFixed(1)}%)</li>}
                            {investmentObjectives.length < 50 && <li>Investment objectives must be at least 50 characters (currently {investmentObjectives.length})</li>}
                        </ul>
                    </div>
                )}
                <div className="flex justify-end space-x-4">
                    <button 
                        className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        onClick={() => {
                            setCustomAllocation({});
                            setInvestmentObjectives('');
                            setLiquidityRequirements(0);
                            setReviewFrequency('annual');
                            setSelectedStrategy('balanced');
                        }}
                    >
                        Reset to Template
                    </button>
                    <button 
                        className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!isValidAllocation || investmentObjectives.length < 50}
                    onClick={() => {
                        try {
                            console.log('Generating PDF...');
                            const doc = new jsPDF();
                            const allocation = customAllocation.cash !== undefined ? customAllocation : strategyTemplates[selectedStrategy].allocation;
                            
                            console.log('jsPDF created, allocation:', allocation);
                            
                            // Title
                            doc.setFontSize(18);
                            doc.setFont(undefined, 'bold');
                            doc.text('SMSF Investment Strategy', 105, 20, { align: 'center' });
                            
                            doc.setFontSize(10);
                            doc.setFont(undefined, 'normal');
                            doc.text(`Generated: ${new Date().toLocaleDateString('en-AU')}`, 105, 28, { align: 'center' });
                            
                            let y = 40;
                            
                            // Fund Details
                            doc.setFontSize(12);
                            doc.setFont(undefined, 'bold');
                            doc.text('1. Investment Objectives', 14, y);
                            y += 8;
                            doc.setFontSize(9);
                            doc.setFont(undefined, 'normal');
                            const splitText = doc.splitTextToSize(investmentObjectives || 'Not specified', 180);
                            doc.text(splitText, 14, y);
                            y += splitText.length * 5 + 10;
                            
                            // Asset Allocation
                            doc.setFontSize(12);
                            doc.setFont(undefined, 'bold');
                            doc.text('2. Asset Allocation', 14, y);
                            y += 8;
                            doc.setFontSize(9);
                            doc.setFont(undefined, 'normal');
                            Object.entries(allocation).forEach(([asset, pct]) => {
                                const assetName = assetClasses[asset]?.name || asset;
                                doc.text(`${assetName}: ${pct}%`, 20, y);
                                y += 5;
                            });
                            y += 5;
                            
                            // Liquidity
                            doc.setFontSize(12);
                            doc.setFont(undefined, 'bold');
                            doc.text('3. Liquidity Requirements', 14, y);
                            y += 8;
                            doc.setFontSize(9);
                            doc.setFont(undefined, 'normal');
                            doc.text(`Minimum liquidity: $${liquidityRequirements.toLocaleString()}`, 20, y);
                            y += 10;
                            
                            // Review
                            doc.setFontSize(12);
                            doc.setFont(undefined, 'bold');
                            doc.text('4. Review Schedule', 14, y);
                            y += 8;
                            doc.setFontSize(9);
                            doc.setFont(undefined, 'normal');
                            doc.text(`Strategy will be reviewed: ${reviewFrequency}`, 20, y);
                            y += 15;
                            
                            // Disclaimer
                            doc.setFontSize(8);
                            doc.setFont(undefined, 'italic');
                            doc.setTextColor(100);
                            const disclaimer = 'This investment strategy was generated using an online tool. Trustees should review and customize as appropriate for their fund. Consider seeking professional advice.';
                            const disclaimerText = doc.splitTextToSize(disclaimer, 180);
                            doc.text(disclaimerText, 14, y);
                            
                            console.log('About to save PDF...');
                            doc.save(`Investment_Strategy_${new Date().toISOString().split('T')[0]}.pdf`);
                            console.log('PDF save called');
                        } catch (error) {
                            console.error('PDF Generation Error:', error);
                            alert('Error generating PDF: ' + error.message);
                        }
                    }}
                >
                    Generate PDF
                </button>
                <button 
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={!isValidAllocation}
                >
                    Save Strategy
                </button>
                </div>
            </div>
        </div>
    );
};

export default SMSFInvestmentStrategyBuilder;